/**
 * カード管理（/cards/<タブ>）。作業ごとにタブを分ける: 取り込み（PDF・読み取り・スキャン済みの登録）/ 登録済みの画像 /
 * 型番・レアリティ（決まらないものを直す・Yugipedia からの取得）/ 点検（重複・言語の食い違い・斜めの画像）。
 * 帯は撮影の画面と同じ `.subnav`。状態はこのページが持つので、タブを行き来しても入力中の下書きは消えない。
 */
import { useState, useEffect, useCallback } from 'react';
import { NavLink, Navigate, useParams } from 'react-router-dom';
import { usePageTitle } from '../appInfo';
import { api } from '../api/api';
import CardCodeReadPanel from '../components/CardCodeReadPanel';
import CardImageManager from '../components/CardImageManager';
import CardRegionZoom, { CARD_REGIONS } from '../components/CardRegionZoom';
import UnresolvedSetCodes from '../components/UnresolvedSetCodes';
import UnresolvedRarities from '../components/UnresolvedRarities';
import CardSetsSyncPanel from '../components/CardSetsSyncPanel';
import CardImageChecks from '../components/CardImageChecks';
import TiltedCardImages from '../components/TiltedCardImages';
import ZoomableImage from '../components/ZoomableImage';
import RarityField, { raritiesFor } from '../components/RarityField';
import { languageOfSetCode } from '../utils/setCodeLanguage';

const PAGE = 100;   // 一度に並べる枚数（スキャンは数が多い）
const STATUS_LABEL = {
  unread: '未読', read: '読み取り済み（未登録）', registered: '登録した', duplicate: '同じ型番が登録済み', unmatched: '要確認', unreadable: '読めない',
};
const FILTERS = { all: 'すべて', unread: '未読', check: '要確認・読めない', duplicate: '重複' };

export const CARD_TABS = [
  { id: 'import', label: '取り込み' },
  { id: 'images', label: '登録済みの画像' },
  { id: 'codes', label: '型番・レアリティ' },
  { id: 'checks', label: '点検' },
];

export default function CardManagement() {
  usePageTitle('カード');
  const { tab } = useParams();
  const [tempImages, setTempImages] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cardIds, setCardIds] = useState({});
  const [langs, setLangs] = useState({});
  const [cardNames, setCardNames] = useState({});
  const [setCodes, setSetCodes] = useState({});       // 画像ごとの型番（Issue #928）
  const [rarities, setRarities] = useState({});       // 画像ごとのレアリティ（Issue #931）
  const [setCodeOptions, setSetCodeOptions] = useState({});   // 画像ごとの型番の候補（そのカードの収録パック）

  const [reads, setReads] = useState({});            // s3 の鍵 → 読み取りの結果（Issue #929）
  const [filter, setFilter] = useState('all');
  const [shown, setShown] = useState(PAGE);
  const [reading, setReading] = useState({});        // 1 枚ずつ読んでいる鍵
  const [jobTick, setJobTick] = useState(0);          // 読み取りのジョブを読み直させる

  const loadTemp = useCallback(() => api.project('GetTempCardImages').then(setTempImages), []);
  const loadReads = useCallback(() => api.project('GetCardCodeReads', { target: 'temp' })
    .then(rows => setReads(Object.fromEntries((rows ?? []).map(r => [r.s3_key, r]))))
    .catch(() => {}), []);
  const [listTick, setListTick] = useState(0);        // 型番が分からなかった一覧を読み直させる
  const reloadAll = useCallback(() => { loadTemp(); loadReads(); setListTick(n => n + 1); }, [loadTemp, loadReads]);
  const bumpList = useCallback(() => setListTick(n => n + 1), []);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  // 入力欄の値: 人が打ったもの → 読み取りの結果（合わなかったものの下書き）
  const cardIdOf = (key) => cardIds[key] ?? reads[key]?.card_id ?? (reads[key]?.passcode ?? '').replace(/\D/g, '').replace(/^0+/, '');
  const setCodeOf = (key) => setCodes[key] ?? reads[key]?.matched ?? reads[key]?.set_code ?? '';
  // 言語は型番の地域で決まればそれ（サーバーも登録で同じに読み替える）→ 人が選んだもの → 読み取りの結果
  const langOf = (key) => languageOfSetCode(setCodeOf(key)) ?? langs[key] ?? reads[key]?.language_code ?? 'jp';
  const rarityOf = (key) => rarities[key] ?? reads[key]?.rarity ?? '';
  const statusOf = (key) => reads[key]?.status ?? 'unread';
  const visible = tempImages.filter(img => filter === 'all' || statusOf(img.key) === filter
    || (filter === 'check' && ['unmatched', 'unreadable'].includes(statusOf(img.key))));
  const duplicates = tempImages.filter(img => statusOf(img.key) === 'duplicate').length;

  const handleDeleteDuplicates = async () => {
    if (!window.confirm(`同じ型番が登録済みの画像 ${duplicates} 枚を消しますか？`)) return;
    try {
      await api.project('DeleteDuplicateTempCardImages');
      reloadAll();
    } catch (err) {
      alert('失敗: ' + err.message);
    }
  };

  const handleUpload = async () => {
    if (!file) { alert('ファイルを選択してください'); return; }
    setUploading(true);
    try {
      const res = await api.project('UploadCardPdf', { filename: file.name });
      await fetch(res.upload_link, {
        method: 'PUT', body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      const done = await api.project('UploadCompleteCardPdf', { s3_key: res.s3_key });
      // 切り出した画像のカード ID と型番はサーバーが裏で読んで入力欄の下書きにする（登録はしない。Issue #929）
      alert(done?.read_request_id
        ? `取り込みました（${done.count} 枚）。カード ID と型番を読み取っています。読めたものから入力欄に入ります`
        : `取り込みました（${done?.count ?? 0} 枚）${done?.read_error ? `。読み取りは始められませんでした: ${done.read_error}` : ''}`);
      loadTemp();
      setJobTick(n => n + 1);
    } catch (err) {
      alert('失敗: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const dropTyped = (key) => [setCardIds, setSetCodes, setLangs, setRarities].forEach(set => set(prev => {
    const { [key]: _, ...rest } = prev;
    return rest;
  }));

  // 1 枚だけその場で読んで入力欄を埋める（登録は人が「登録」で。Issue #929）
  const handleRead = async (img) => {
    setReading(prev => ({ ...prev, [img.key]: true }));
    try {
      const res = await api.project('ReadCardCodes', { s3_key: img.key });
      setReads(prev => ({ ...prev, [img.key]: res }));
      dropTyped(img.key);
      if (res.card_id) await showCard(img.key, res.card_id);
    } catch (err) {
      alert('読み取りに失敗しました: ' + err.message);
    } finally {
      setReading(prev => ({ ...prev, [img.key]: false }));
    }
  };

  const handlePreview = async (img) => {
    const cardId = cardIdOf(img.key);
    if (!cardId) { alert('カードIDを入力してください'); return; }
    await showCard(img.key, cardId);
  };

  const showCard = async (key, cardId) => {
    try {
      const res = await api.card('GetCardInfo', { card_id: String(cardId) });
      setCardNames(prev => ({ ...prev, [key]: res.name ?? '不明' }));
    } catch {
      setCardNames(prev => ({ ...prev, [key]: '不明' }));
    }
    try {
      const codes = await api.project('GetCardSetCodes', { card_id: String(cardId) });
      setSetCodeOptions(prev => ({ ...prev, [key]: Array.isArray(codes) ? codes : [] }));
    } catch { /* 候補は無くてよい */ }
  };

  const handleRegister = async (img) => {
    const cardId = cardIdOf(img.key);
    const lang = langOf(img.key);
    if (!cardId) { alert('カードIDを入力してください'); return; }
    try {
      await api.project('UploadCardImageInternal', {
        card_id: String(cardId), language_code: lang, s3_key: img.key, set_code: setCodeOf(img.key), rarity: rarityOf(img.key),
      });
      alert('登録しました');
      loadTemp();
    } catch (err) {
      alert('失敗: ' + err.message);
    }
  };

  const handleDelete = async (img) => {
    if (!window.confirm('削除しますか？')) return;
    try {
      await api.project('DeleteTempCardImage', { s3_key: img.key });
      setTempImages(prev => prev.filter(i => i.key !== img.key));
    } catch (err) {
      alert('失敗: ' + err.message);
    }
  };

  if (!CARD_TABS.some(t => t.id === tab)) return <Navigate to="/cards/import" replace />;

  return (
    <>
    <nav className="subnav" aria-label="カード管理" data-testid="card-nav">
      {CARD_TABS.map(t => (
        <NavLink key={t.id} to={`/cards/${t.id}`}>
          {t.label}{t.id === 'import' && tempImages.length > 0 && `（${tempImages.length}）`}
        </NavLink>
      ))}
    </nav>
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">カード管理</h1>
      </div>

      {tab === 'import' && <>
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-title" style={{ marginBottom: 12 }}>カードPDFをアップロード</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept=".pdf" onChange={e => setFile(e.target.files[0])} style={{ color: '#aaa', fontSize: '0.9rem' }} />
          <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? 'アップロード中...' : 'アップロード'}
          </button>
        </div>
      </div>

      <CardCodeReadPanel onProgress={loadReads} onFinished={reloadAll} refreshKey={jobTick} />

      </>}

      {tab === 'images' && <CardImageManager />}

      {tab === 'import' && <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ fontSize: '1rem', margin: 0, color: '#aaa' }}>
          スキャン済み画像一覧 ({visible.length} / {tempImages.length}件)
        </h2>
        <select className="form-select" style={{ width: 160 }} value={filter} data-testid="temp-filter"
          onChange={e => { setFilter(e.target.value); setShown(PAGE); }}>
          {Object.entries(FILTERS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        {duplicates > 0 && (
          <button className="btn btn-danger btn-sm" onClick={handleDeleteDuplicates}>重複 {duplicates} 枚をまとめて削除</button>
        )}
      </div>

      {visible.length === 0 && <p className="loading">画像がありません</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {visible.slice(0, shown).map(img => (
          <div key={img.key} className="card" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <ZoomableImage src={img.url} alt={img.key} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                <CardRegionZoom src={img.url} region={CARD_REGIONS.passcode} title="カードID拡大（左下）" label="カード ID" />
                <CardRegionZoom src={img.url} region={CARD_REGIONS.setCode} width={280} title="型番拡大（絵の右下）" label="型番"
                  testId={`temp-set-code-zoom-${img.key}`} />
                <input
                  className="form-input"
                  style={{ width: 140 }}
                  placeholder="カードID"
                  value={cardIdOf(img.key)}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').replace(/^0+/, '');
                    setCardIds(prev => ({ ...prev, [img.key]: v }));
                  }}
                />
                <select
                  className="form-select"
                  style={{ width: 110 }}
                  value={langOf(img.key)}
                  disabled={!!languageOfSetCode(setCodeOf(img.key))}
                  title={languageOfSetCode(setCodeOf(img.key)) ? '型番から決まる言語' : undefined}
                  data-testid={`temp-lang-${img.key}`}
                  onChange={e => setLangs(prev => ({ ...prev, [img.key]: e.target.value }))}
                >
                  <option value="jp">日本語</option>
                  <option value="en">English</option>
                </select>
                <input
                  className="form-input"
                  style={{ width: 160 }}
                  placeholder="型番（任意）"
                  list={`temp-set-codes-${img.key}`}
                  value={setCodeOf(img.key)}
                  onChange={e => setSetCodes(prev => ({ ...prev, [img.key]: e.target.value }))}
                />
                <datalist id={`temp-set-codes-${img.key}`}>
                  {(setCodeOptions[img.key] ?? []).map(c => <option key={c.set_code} value={c.set_code}>{c.pack_name ?? ''}</option>)}
                </datalist>
                <RarityField id={`temp-rarity-${img.key}`} testId={`temp-rarity-${img.key}`} value={rarityOf(img.key)}
                  options={raritiesFor(setCodeOptions[img.key], setCodeOf(img.key))}
                  onChange={v => setRarities(prev => ({ ...prev, [img.key]: v }))} />
                <button className="btn btn-secondary btn-sm" disabled={!!reading[img.key]} data-testid={`read-one-${img.key}`}
                  onClick={() => handleRead(img)}>{reading[img.key] ? '読み取り中…' : '読み取る'}</button>
                <button className="btn btn-secondary btn-sm" onClick={() => handlePreview(img)}>名前確認</button>
              </div>
              {reads[img.key] && (
                <div className="card-meta" style={{ marginBottom: 8 }} data-testid={`read-${img.key}`}>
                  読み取り: <strong>{STATUS_LABEL[statusOf(img.key)]}</strong>
                  {reads[img.key].note && `（${reads[img.key].note}）`}
                </div>
              )}
              {cardNames[img.key] && (
                <div className="card-meta" style={{ marginBottom: 8 }}>
                  カード名: <strong>{cardNames[img.key]}</strong>
                </div>
              )}
              <div className="actions">
                <button className="btn btn-primary btn-sm" onClick={() => handleRegister(img)}>登録</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(img)}>削除</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {visible.length > shown && (
        <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={() => setShown(n => n + PAGE)}>
          もっと見る（残り {visible.length - shown} 件）
        </button>
      )}
      </>}

      {tab === 'codes' && <>
        <UnresolvedSetCodes refreshKey={listTick}
          tempCount={tempImages.filter(img => ['unmatched', 'unreadable'].includes(statusOf(img.key))).length} />
        <CardSetsSyncPanel onDone={bumpList} />
        <UnresolvedRarities refreshKey={listTick} />
      </>}

      {tab === 'checks' && <>
        <CardImageChecks refreshKey={listTick} />
        <TiltedCardImages refreshKey={listTick} />
      </>}
    </div>
    </>
  );
}
